import { acceptHMRUpdate, defineStore } from 'pinia'
import axios from 'axios'
import { BASE_URL } from '@/environment/environment'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { onboardingApiUrl } from '@/modules/onboarding/utils/demoApi'
import type { IAppealChannel, IAppealDetail, IAppealListItem, IAppealModuleStatus } from '../types/IAppeal'
import { getEmptyAppealFilters } from '../types/IAppealFilters'
import type {
  IAppealChannelPayload,
  IAppealChannelsListResponse,
  IAppealQrResponse,
  IAppealReplyPayload,
  IAppealsListResponse,
  ICreateAppealPayload,
  IFilterChoicesResponse,
  IUpdateAppealPayload,
} from '../types/IAppealPayloads'
import type { ICitizenInquiriesStoreState } from '../types/ICitizenInquiriesStoreState'
import type { IResponsibilityListItem } from '@/modules/tasks/types/IResponsibilityListItem'
/**
 * Хранилище обращений граждан (useCitizenInquiriesStore)
 *
 * Управляет:
 * - Проверкой блокировки модуля обращений (соглашение ПД)
 * - Загрузкой списка обращений с серверной фильтрацией и курсорной пагинацией
 * - Просмотром, созданием и редактированием карточки обращения
 * - Управлением справочником каналов поступления
 * - Назначением/сменой координатора обращения
 * - Отправкой ответа на обращение по электронной почте
 * - Получением данных QR-кода публичной формы
 */
export const useCitizenInquiriesStore = defineStore('citizenInquiriesStore', {
  state: (): ICitizenInquiriesStoreState => ({
    items: [],
    totalCount: 0,
    nextCursor: null,
    previousCursor: null,
    isLoading: false,
    currentAppeal: null,
    isLoadingDetail: false,
    isReplying: false,
    isSaving: false,
    moduleStatus: null,
    isModuleStatusLoading: false,
    channels: [],
    createChannels: [],
    isChannelsLoading: false,
    filters: getEmptyAppealFilters(),
    filterChoices: {
      topic: [],
      status: [],
      channels: [],
    },
    searchChoices: {
      topic: [],
      status: [],
      channels: [],
    },
    filterCursors: {
      topic: null,
      status: null,
      channels: null,
    },
    filterStrategies: {
      topic: 'independent',
      status: 'independent',
      channels: 'independent',
    },
    filterLoading: {
      topic: false,
      status: false,
      channels: false,
    },
    qrData: null,
    isQrLoading: false,
    itemsPerPage: 25,
    currentSortString: null,
  }),

  actions: {
    /**
     * Проверка статуса доступности модуля обращений (наличие синхронизированного соглашения ПД)
     */
    async fetchModuleStatus(): Promise<IAppealModuleStatus | null> {
      this.isModuleStatusLoading = true
      try {
        const response = await axios.get<IAppealModuleStatus>(`${BASE_URL}/api/appeals/module-status/`)
        this.moduleStatus = response.data
        return response.data
      } catch (error) {
        console.error('Ошибка при проверке статуса модуля обращений:', error)
        return null
      } finally {
        this.isModuleStatusLoading = false
      }
    },

    /**
     * Загрузка списка обращений граждан с поддержкой фильтров и пагинации
     */
    async fetchAppeals(cursor?: string, limit?: number) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      this.isLoading = true

      try {
        const params: Record<string, any> = {}

        if (this.currentSortString) {
          params.sort = this.currentSortString
        }

        const pageSize = limit || this.itemsPerPage
        if (pageSize) {
          params.page_size = pageSize
        }

        if (this.filters.search?.trim()) {
          params.search = this.filters.search.trim()
        }

        if (this.filters.numbers?.length) {
          params.numbers = this.filters.numbers.join(',')
        }

        if (Array.isArray(this.filters.topic) && this.filters.topic.length > 0) {
          params.topic = this.filters.topic.join(',')
        } else if (this.filters.topic && !Array.isArray(this.filters.topic)) {
          params.topic = this.filters.topic
        }

        if (Array.isArray(this.filters.status) && this.filters.status.length > 0) {
          params.status = this.filters.status.join(',')
        } else if (this.filters.status && !Array.isArray(this.filters.status)) {
          params.status = this.filters.status
        }

        if (Array.isArray(this.filters.channels) && this.filters.channels.length > 0) {
          params.channels = this.filters.channels.join(',')
        } else if (this.filters.channels && !Array.isArray(this.filters.channels)) {
          params.channels = this.filters.channels
        }

        if (this.filters.created_from) {
          params.created_from = this.filters.created_from
        }

        if (this.filters.created_to) {
          params.created_to = this.filters.created_to
        }

        if (this.filters.is_actual !== null) {
          params.is_actual = this.filters.is_actual
        }

        if (cursor) {
          params.cursor = cursor
        }

        const response = await axios.get<IAppealsListResponse>(`${BASE_URL}/api/appeals/`, {
          params,
        })

        const data = response.data
        const results: IAppealListItem[] = data.results || []

        if (cursor) {
          this.items = [...this.items, ...results]
        } else {
          this.items = results
          this.totalCount = data.count ?? results.length
        }

        // Извлечение курсора из next-ссылки при необходимости
        if (data.next) {
          try {
            const urlObj = new URL(data.next, window.location.origin)
            this.nextCursor = urlObj.searchParams.get('cursor') || data.next
          } catch {
            this.nextCursor = data.next
          }
        } else {
          this.nextCursor = null
        }

        if (data.previous) {
          try {
            const urlObj = new URL(data.previous, window.location.origin)
            this.previousCursor = urlObj.searchParams.get('cursor') || data.previous
          } catch {
            this.previousCursor = data.previous
          }
        } else {
          this.previousCursor = null
        }
      } catch (error: any) {
        if (error?.response?.status === 403) {
          await this.fetchModuleStatus()
          if (this.moduleStatus?.is_locked) {
            return
          }
        }
        console.error('Ошибка при загрузке обращений граждан:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить список обращений'),
          time: 5000,
        })
      } finally {
        this.isLoading = false
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загрузка детальной информации об обращении по его ID
     */
    async fetchAppealById(id: string | number) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      this.isLoadingDetail = true

      try {
        const response = await axios.get<IAppealDetail>(`${BASE_URL}/api/appeals/${id}/`)

        this.currentAppeal = response.data
        return response.data
      } catch (error) {
        console.error(`Ошибка при загрузке обращения ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить данные обращения'),
          time: 5000,
        })
        throw error
      } finally {
        this.isLoadingDetail = false
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Ручное создание обращения гражданина
     */
    async createAppeal(payload: ICreateAppealPayload): Promise<IAppealDetail> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      this.isSaving = true

      try {
        const response = await axios.post<IAppealDetail>(`${BASE_URL}/api/appeals/`, payload)
        this.currentAppeal = response.data
        feedbackStore.showToast({
          type: 'success',
          message: 'Обращение успешно создано',
          time: 4000,
        })
        await this.fetchAppeals()
        return response.data
      } catch (error) {
        console.error('Ошибка при создании обращения:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать обращение'),
          time: 5000,
        })
        throw error
      } finally {
        this.isSaving = false
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обновление данных обращения гражданина
     */
    async updateAppeal(id: string | number, payload: IUpdateAppealPayload): Promise<IAppealDetail> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      this.isSaving = true

      try {
        const response = await axios.patch<IAppealDetail>(`${BASE_URL}/api/appeals/${id}/`, payload)
        this.currentAppeal = response.data

        const idx = this.items.findIndex(item => String(item.id) === String(id) || (item.local_id && String(item.local_id) === String(id)))
        if (idx !== -1) {
          this.items[idx] = {
            ...this.items[idx],
            ...response.data,
          }
        }

        feedbackStore.showToast({
          type: 'success',
          message: 'Обращение успешно обновлено',
          time: 4000,
        })
        return response.data
      } catch (error) {
        console.error(`Ошибка при обновлении обращения ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить обращение'),
          time: 5000,
        })
        throw error
      } finally {
        this.isSaving = false
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Назначение / смена координатора обращения гражданина
     */
    async changeAppealCoordinator(payload: {
      appeal_id: string | number
      coordinator_id: string
    }): Promise<IResponsibilityListItem> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const response = await axios.patch<IResponsibilityListItem>(
          `${BASE_URL}/api/appeals/${payload.appeal_id}/responsibility-entries/change-coordinator/`,
          { coordinator_id: payload.coordinator_id },
        )

        const updatedEntry = response.data

        if (this.currentAppeal && (String(this.currentAppeal.id) === String(payload.appeal_id) || String(this.currentAppeal.local_id) === String(payload.appeal_id))) {
          this.currentAppeal.coordinator_entry = updatedEntry
          if (updatedEntry?.responsible_employee) {
            this.currentAppeal.coordinator = updatedEntry.responsible_employee as any
          }
        }

        const idx = this.items.findIndex(item => String(item.id) === String(payload.appeal_id) || (item.local_id && String(item.local_id) === String(payload.appeal_id)))
        if (idx !== -1 && updatedEntry?.responsible_employee) {
          this.items[idx] = {
            ...this.items[idx],
            coordinator: updatedEntry.responsible_employee as any,
          }
        }

        feedbackStore.showToast({
          type: 'success',
          message: 'Координатор успешно назначен',
          time: 4000,
        })
        return updatedEntry
      } catch (error) {
        console.error('Ошибка при смене координатора обращения:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить координатора обращения'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загрузка справочника каналов поступления обращений
     */
    async fetchChannels(forCreate = false): Promise<IAppealChannel[]> {
      this.isChannelsLoading = true
      try {
        const params: Record<string, any> = {}
        if (forCreate) {
          params.for_create = '1'
        }
        const response = await axios.get<IAppealChannelsListResponse | IAppealChannel[]>(
          onboardingApiUrl('appeal', '/appeals/channels/'),
          { params },
        )
        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : []

        if (forCreate) {
          this.createChannels = list
        } else {
          this.channels = list
        }
        return list
      } catch (error) {
        console.error('Ошибка при загрузке каналов обращений:', error)
        return []
      } finally {
        this.isChannelsLoading = false
      }
    },

    /**
     * Создание канала поступления обращений
     */
    async createChannel(payload: IAppealChannelPayload): Promise<IAppealChannel> {
      const feedbackStore = useFeedbackStore()
      try {
        const response = await axios.post<IAppealChannel>(`${BASE_URL}/api/appeals/channels/`, payload)
        feedbackStore.showToast({
          type: 'success',
          message: 'Канал добавлен',
          time: 3000,
        })
        await Promise.all([this.fetchChannels(false), this.fetchChannels(true)])
        return response.data
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать канал'),
          time: 5000,
        })
        throw error
      }
    },

    /**
     * Обновление канала поступления обращений
     */
    async updateChannel(id: number, payload: Partial<IAppealChannelPayload>): Promise<IAppealChannel> {
      const feedbackStore = useFeedbackStore()
      try {
        const response = await axios.patch<IAppealChannel>(`${BASE_URL}/api/appeals/channels/${id}/`, payload)
        feedbackStore.showToast({
          type: 'success',
          message: 'Канал обновлён',
          time: 3000,
        })
        await Promise.all([this.fetchChannels(false), this.fetchChannels(true)])
        return response.data
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить канал'),
          time: 5000,
        })
        throw error
      }
    },

    /**
     * Удаление канала поступления обращений
     */
    async deleteChannel(id: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      try {
        await axios.delete(`${BASE_URL}/api/appeals/channels/${id}/`)
        feedbackStore.showToast({
          type: 'success',
          message: 'Канал удалён',
          time: 3000,
        })
        await Promise.all([this.fetchChannels(false), this.fetchChannels(true)])
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить канал'),
          time: 5000,
        })
        throw error
      }
    },

    /**
     * Загрузка вариантов для выпадающих фильтров (тема, статус, каналы)
     */
    async fetchFilterChoices(field: string, search?: string, cursor?: string) {
      this.filterLoading[field] = true

      try {
        const params: Record<string, any> = { field }
        if (search) {
          params.search_field = search
        }
        if (cursor) {
          params.cursor = cursor
        }

        if (field !== 'topic' && this.filters.topic?.length) {
          params.topic = this.filters.topic.join(',')
        }
        if (field !== 'status' && this.filters.status?.length) {
          params.status = this.filters.status.join(',')
        }
        if (field !== 'channels' && this.filters.channels?.length) {
          params.channels = this.filters.channels.join(',')
        }
        if (this.filters.numbers?.length) {
          params.numbers = this.filters.numbers.join(',')
        }
        if (this.filters.created_from) {
          params.created_from = this.filters.created_from
        }
        if (this.filters.created_to) {
          params.created_to = this.filters.created_to
        }
        if (this.filters.is_actual !== null) {
          params.is_actual = this.filters.is_actual
        }

        const response = await axios.get<IFilterChoicesResponse>(onboardingApiUrl('appeal', '/appeals/filter-choices/'), {
          params,
        })

        const results = response.data.results || []
        const next = (response.data as any).next || null
        this.filterStrategies[field] = response.data.strategy || 'independent'

        if (search) {
          if (cursor) {
            this.searchChoices[field] = [...(this.searchChoices[field] || []), ...results]
          } else {
            this.searchChoices[field] = results
          }
        } else {
          if (cursor) {
            this.filterChoices[field] = [...(this.filterChoices[field] || []), ...results]
          } else {
            this.filterChoices[field] = results
          }
        }
        this.filterCursors[field] = next
      } catch (error) {
        console.error(`Ошибка при загрузке вариантов фильтра ${field}:`, error)
        if (search) {
          this.searchChoices[field] = []
        } else {
          this.filterChoices[field] = []
        }
      } finally {
        this.filterLoading[field] = false
      }
    },

    /**
     * Отправка ответа на обращение гражданина
     */
    async replyAppeal(id: string | number, payload: IAppealReplyPayload) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      this.isReplying = true

      try {
        const response = await axios.post<IAppealDetail>(
          `${BASE_URL}/api/appeals/${id}/reply/`,
          payload,
        )

        feedbackStore.showToast({
          type: 'success',
          message: 'Ответ успешно отправлен заявителю',
          time: 4000,
        })

        // Обновляем текущую деталку
        if (this.currentAppeal && (String(this.currentAppeal.id) === String(id) || String(this.currentAppeal.local_id) === String(id))) {
          this.currentAppeal = {
            ...this.currentAppeal,
            ...response.data,
            status: 'answered',
            reply_text: response.data?.reply_text || payload.body,
            replied_at: response.data?.replied_at || new Date().toISOString(),
            email: response.data?.email || payload.email || this.currentAppeal.email,
          }
        }

        // Обновляем запись в списке
        const idx = this.items.findIndex(item => String(item.id) === String(id) || (item.local_id && String(item.local_id) === String(id)))
        if (idx !== -1) {
          this.items[idx] = {
            ...this.items[idx],
            ...response.data,
            status: 'answered',
            reply_text: response.data?.reply_text || payload.body,
            replied_at: response.data?.replied_at || new Date().toISOString(),
            email: response.data?.email || payload.email || this.items[idx].email,
          }
        }

        return response.data
      } catch (error) {
        console.error(`Ошибка при отправке ответа на обращение ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось отправить ответ на обращение'),
          time: 5000,
        })
        throw error
      } finally {
        this.isReplying = false
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загрузка данных QR-кода публичной формы обращений
     */
    async fetchQrCode() {
      const feedbackStore = useFeedbackStore()
      this.isQrLoading = true

      try {
        const response = await axios.get<IAppealQrResponse>(`${BASE_URL}/api/appeals/qr/`)

        this.qrData = response.data
        return response.data
      } catch (error) {
        console.error('Ошибка при загрузке QR-кода обращений:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить QR-код'),
          time: 5000,
        })
        throw error
      } finally {
        this.isQrLoading = false
      }
    },

    /**
     * Сброс всех фильтров
     */
    // Переключатель «Скрыть отвеченные» (is_actual) сбросом не трогается
    resetFilters() {
      this.filters = { ...getEmptyAppealFilters(), is_actual: this.filters.is_actual }
      this.currentSortString = null
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCitizenInquiriesStore, import.meta.hot))
}
